import {
  Button,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Drawer,
  MenuItem,
} from '@mui/material'
import './ActionBar.css'
import { DeleteOutlined, EditOutlined, LayersOutlined, PriceChangeOutlined } from '@mui/icons-material'
import { quoteLineType } from '../types'
import { useState } from 'react'
import { CurrencyInput } from '../helper'
import { LineEdit } from '../EditQuote/LineEdit'
import axios from 'axios'
import { useParams } from 'react-router-dom'

export default function ActionBar(props: {
  lineItems: (quoteLineType & { children: quoteLineType[] })[]
  setLineItems: (lineItems: (quoteLineType & { children: quoteLineType[] })[]) => void
  selectedLineItems: quoteLineType[]
  setSelectedLineItems: (lineItems: quoteLineType[]) => void
  setBulkEditSnackbar: (snackbar: 'success' | 'fail' | null) => void
  setBundleSnackbar: (snackbar: 'success' | 'fail' | null) => void
  lineItemCount: number
}) {
  const [openDialog, setOpenDialog] = useState<'price' | 'bundle' | 'edit' | 'delete' | null>(null)
  const { quoteId } = useParams()
  const [amount, setAmount] = useState<number>(0)
  const [pricingMethod, setPricingMethod] = useState<string | null>(null)
  const [bundleName, setBundleName] = useState<string>('')
  const [bundleDescription, setBundleDescription] = useState<string>('')

  const handleBulkEdit = async (lineItems: quoteLineType[]) => {
    const previousLineItems = [...props.lineItems] as (quoteLineType & { children: quoteLineType[] })[]
    axios
      .post(`/bulkEditLineItems/${quoteId}`, { lineItems: lineItems })
      .then(res => {
        props.setBulkEditSnackbar('success')
        props.setLineItems(res.data)
      })
      .catch(err => {
        console.error(err)
        props.setLineItems(previousLineItems)
        props.setBulkEditSnackbar('fail')
      })
  }

  const flattenBundledStructure = (
    bundledItems: (quoteLineType & { children: quoteLineType[] })[],
  ): quoteLineType[] => {
    return bundledItems.flatMap(bundle => {
      if (bundle.LineNumber === -1) {
        return [bundle, ...bundle.children]
      } else {
        return [bundle]
      }
    })
  }

  const handleDelete = (lineItemsToDelete: quoteLineType[]) => {
    const flatAllItems = flattenBundledStructure(props.lineItems)
    const itemsToDeleteIds = new Set(lineItemsToDelete.map(item => item.LineItemId))

    const bundlesToCheck = new Set(lineItemsToDelete.filter(item => item.Bundle).map(item => item.Bundle)) as Set<
      string | null
    >
    bundlesToCheck.delete(null)

    for (const bundleId of bundlesToCheck) {
      if (!bundleId) continue
      const bundleInStructure = props.lineItems.find(item => item.Bundle === bundleId && item.LineNumber === -1)
      if (bundleInStructure) {
        const childrenInBundle = bundleInStructure.children
        const childrenToDelete = lineItemsToDelete.filter(item => item.Bundle === bundleId && item.LineNumber !== -1)
        if (childrenToDelete.length === childrenInBundle.length) {
          itemsToDeleteIds.add(bundleInStructure.LineItemId)
        }
      }
    }

    const updatedLineItems = flatAllItems.filter(item => !itemsToDeleteIds.has(item.LineItemId))

    handleBulkEdit(updatedLineItems)
      .then(() => {
        props.setSelectedLineItems([])
      })
      .catch(err => {
        console.error(err)
      })
  }

  const calculateMargin = (
    unitPrice: number,
    unitCost: number,
    totalFees: number,
    creditCard: boolean | null,
  ): number => {
    if (unitPrice === 0) return 0
    const costWithFees = unitCost + totalFees + (creditCard ? unitCost * 0.03 : 0)
    return (1 - costWithFees / unitPrice) * 100
  }

  const handleEditPrice = (amount: number) => {
    const flatAllItems = flattenBundledStructure(props.lineItems)
    const selectedLineItemIds = new Set(props.selectedLineItems.map(item => item.LineItemId))

    const updatedLineItems = flatAllItems.map(item => {
      if (selectedLineItemIds.has(item.LineItemId) && item.LineNumber !== -1) {
        let updatedItem = { ...item }

        if (pricingMethod === 'Cost + %') {
          const unitPrice = (item.UnitCost ?? 0) * (1 + amount / 100)
          const margin = calculateMargin(unitPrice, item.UnitCost ?? 0, item.TotalFees ?? 0, item.CreditCard)
          updatedItem = {
            ...updatedItem,
            UnitPrice: unitPrice,
            ExtPrice: unitPrice * (item.Qty ?? 0),
            Margin: margin,
          }
        } else if (pricingMethod === 'Cost + $') {
          const unitPrice = (item.UnitCost ?? 0) + amount
          const margin = calculateMargin(unitPrice, item.UnitCost ?? 0, item.TotalFees ?? 0, item.CreditCard)
          updatedItem = {
            ...updatedItem,
            UnitPrice: unitPrice,
            ExtPrice: unitPrice * (item.Qty ?? 0),
            Margin: margin,
          }
        } else if (pricingMethod === 'Gross Profit (GP)') {
          const unitPrice =
            ((item.UnitCost ?? 0) + (item.TotalFees ?? 0) + (item.CreditCard ? (item.UnitCost ?? 0) * 0.03 : 0)) /
            (1 - amount / 100)
          updatedItem = {
            ...updatedItem,
            Margin: amount,
            UnitPrice: unitPrice,
            ExtPrice: unitPrice * (item.Qty ?? 0),
          }
        } else if (pricingMethod === 'Manual') {
          const margin = calculateMargin(amount, item.UnitCost ?? 0, item.TotalFees ?? 0, item.CreditCard)
          updatedItem = {
            ...updatedItem,
            UnitPrice: amount,
            ExtPrice: amount * (item.Qty ?? 0),
            Margin: margin,
          }
        }

        return updatedItem
      }
      return item
    })

    handleBulkEdit(updatedLineItems)
  }

  const handleBundle = async (lineItems: quoteLineType[]) => {
    const previousLineItems = [...lineItems] as (quoteLineType & { children: quoteLineType[] })[]
    axios
      .post(`/bundleLineItems/${quoteId}`, { name: bundleName, description: bundleDescription, lineItems: lineItems })
      .then(res => {
        props.setLineItems(res.data)
        props.setBundleSnackbar('success')
        props.setSelectedLineItems([])
      })
      .catch(err => {
        console.error(err)
        props.setLineItems(previousLineItems)
        props.setBundleSnackbar('fail')
      })
  }

  return (
    <div
      className="action-bar"
      style={{
        opacity: props.selectedLineItems.length > 0 ? 1 : 0,
        visibility: props.selectedLineItems.length > 0 ? 'visible' : 'hidden',
      }}
    >
      <Typography variant="body1">
        {props.selectedLineItems.length} of {props.lineItemCount} selected
      </Typography>
      <Divider orientation="vertical" flexItem color="white" />
      <Button
        color="inherit"
        size="large"
        variant="text"
        startIcon={<PriceChangeOutlined />}
        onClick={() => setOpenDialog('price')}
      >
        Edit Price
      </Button>
      <Button
        color="inherit"
        size="large"
        variant="text"
        startIcon={<LayersOutlined />}
        onClick={() => setOpenDialog('bundle')}
      >
        Bundling
      </Button>
      <Button
        color="inherit"
        size="large"
        variant="text"
        startIcon={<EditOutlined />}
        onClick={() => {
          setOpenDialog('edit')
        }}
      >
        Bulk Edit
      </Button>
      <Button
        color="inherit"
        size="large"
        variant="text"
        startIcon={<DeleteOutlined />}
        onClick={() => setOpenDialog('delete')}
      >
        Delete
      </Button>

      <Dialog open={openDialog === 'price'} onClose={() => setOpenDialog(null)}>
        <DialogTitle variant="h6">Edit Price</DialogTitle>
        <DialogContent className="action-bar-dialog-content">
          <Typography variant="body1">All fields are required.</Typography>
          <TextField
            required
            size="small"
            fullWidth
            label="Pricing Method"
            value={pricingMethod}
            onChange={e => setPricingMethod(e.target.value)}
            select
          >
            <MenuItem value="Cost + %">Cost + %</MenuItem>
            <MenuItem value="Cost + $">Cost + $</MenuItem>
            <MenuItem value="Gross Profit (GP)">Gross Profit (GP)</MenuItem>
            <MenuItem value="Manual">Manual</MenuItem>
          </TextField>
          {(pricingMethod === 'Cost + %' || pricingMethod === 'Gross Profit (GP)') && (
            <TextField
              required
              size="small"
              fullWidth
              label="Amount (%)"
              value={amount}
              onChange={e => setAmount(Number(e.target.value))}
            />
          )}
          {(pricingMethod === 'Cost + $' || pricingMethod === 'Manual') && (
            <CurrencyInput
              required
              value={amount}
              onChange={value => setAmount(value ?? 0)}
              size="small"
              fullWidth
              label="Amount ($)"
            />
          )}
        </DialogContent>
        <DialogActions className="action-bar-dialog-actions">
          <Button onClick={() => setOpenDialog(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleEditPrice(Number(amount))
              setOpenDialog(null)
            }}
            variant="contained"
            color="primary"
            disabled={!pricingMethod}
          >
            Edit Price
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog === 'bundle'} onClose={() => setOpenDialog(null)}>
        <DialogTitle variant="h6">Create Bundle</DialogTitle>
        <DialogContent className="action-bar-dialog-content">
          <Typography variant="body1">
            All fields are required.
            {props.selectedLineItems.some(lineItem => lineItem.Bundle)
              ? 'Some of the selected line items are already part of another bundle. Creating this bundle will move them into the new one.'
              : ''}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label="Bundle Name"
            value={bundleName}
            onChange={e => setBundleName(e.target.value)}
          />
          <TextField
            size="small"
            fullWidth
            label="Bundle Description"
            value={bundleDescription}
            onChange={e => setBundleDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions className="action-bar-dialog-actions">
          <Button onClick={() => setOpenDialog(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleBundle(props.selectedLineItems)
              setOpenDialog(null)
            }}
            variant="contained"
            color="primary"
            disabled={!bundleName || !bundleDescription}
          >
            Create Bundle
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog === 'delete'} onClose={() => setOpenDialog(null)}>
        <DialogTitle variant="h6">
          Delete {props.selectedLineItems.length} Line Item{props.selectedLineItems.length > 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent className="action-bar-dialog-content">
          <Typography variant="body1">
            Are you sure you want to delete the selected line item{props.selectedLineItems.length > 1 ? 's' : ''}? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions className="action-bar-dialog-actions">
          <Button onClick={() => setOpenDialog(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDelete(props.selectedLineItems)
              setOpenDialog(null)
            }}
            variant="contained"
            color="primary"
          >
            Delete Line Items
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer open={openDialog === 'edit'} onClose={() => setOpenDialog(null)} anchor="right">
        <LineEdit
          open={openDialog === 'edit'}
          onClose={() => setOpenDialog(null)}
          fullLineItems={props.lineItems as (quoteLineType & { children: quoteLineType[] })[]}
          setLineItems={props.setLineItems}
          selectedLineItems={props.selectedLineItems}
          quoteId={quoteId ?? ''}
          type="bulk"
        />
      </Drawer>
    </div>
  )
}
